/**
 * Explicit product-mode authority for Speaking exams.
 *
 * Deliberate Trial must remain guest regardless of access_token presence,
 * expiry, login, or entitlement. Full Exam / Individual Task keep paid
 * bootstrap when the user is authenticated.
 */

export const EXAM_MODE_STORAGE_KEY = "exam_mode";

export type ExamMode = "trial" | "full";

export type SessionBootstrapPath = "guest" | "paid";

export type SessionBootstrapPlan =
  | { action: "startGuestExam" }
  | { action: "startPaidExam" }
  | { action: "reuse"; sessionId: string };

function readSessionStorage(key: string): string | null {
  try {
    if (typeof sessionStorage === "undefined") return null;
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSessionStorage(key: string, value: string): void {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(key, value);
  } catch {
    // Private mode / blocked storage must not break exam entry.
  }
}

export function isExamMode(value: unknown): value is ExamMode {
  return value === "trial" || value === "full";
}

export function setExamMode(mode: ExamMode): void {
  writeSessionStorage(EXAM_MODE_STORAGE_KEY, mode);
}

export function getExamMode(): ExamMode | null {
  const value = readSessionStorage(EXAM_MODE_STORAGE_KEY);
  return isExamMode(value) ? value : null;
}

export function isDeliberateTrial(): boolean {
  return getExamMode() === "trial";
}

export function parseExamModeFromSearch(search: string | null | undefined): ExamMode | null {
  if (!search) return null;
  try {
    const params = new URLSearchParams(
      search.startsWith("?") ? search.slice(1) : search
    );
    const mode = params.get("mode");
    return isExamMode(mode) ? mode : null;
  } catch {
    return null;
  }
}

/**
 * Capture explicit Trial vs Full Exam from the current entry navigation.
 *
 * Explicit signals win: `state.examMode` then `?mode=`. Otherwise an existing
 * stored mode is preserved. A missing mode is NOT silently defaulted — the
 * caller must opt in via `defaultMode` (only the public Trial entry does this).
 * This prevents a generic route (e.g. /task-one re-entry) from converting an
 * existing paid exam into Trial merely because sessionStorage is empty.
 */
export function captureExamModeFromEntry(input: {
  search?: string | null;
  stateExamMode?: unknown;
  defaultMode?: ExamMode;
}): ExamMode | null {
  if (isExamMode(input.stateExamMode)) {
    setExamMode(input.stateExamMode);
    return input.stateExamMode;
  }

  const fromQuery = parseExamModeFromSearch(input.search);
  if (fromQuery) {
    setExamMode(fromQuery);
    return fromQuery;
  }

  const existing = getExamMode();
  if (existing) return existing;

  if (input.defaultMode) {
    setExamMode(input.defaultMode);
    return input.defaultMode;
  }

  return null;
}

/**
 * Deliberate-Trial decision for task pages and subscription gating.
 *
 * An Individual Task / retry re-entry (examId present) is never a Trial, so a
 * stale `exam_mode` alone can never bypass entitlement. A concrete paid/existing
 * authority (`current_exam_id`, an `exam_*` id) also disqualifies Trial. Only an
 * actual Trial flow (no examId + no paid authority + mode "trial") is treated as
 * deliberate Trial.
 */
export function isTaskDeliberateTrial(input: {
  hasLocationExamId: boolean;
  hasCurrentExamId?: boolean;
  examMode: ExamMode | null;
}): boolean {
  if (input.hasLocationExamId) return false;
  if (input.hasCurrentExamId) return false;
  return input.examMode === "trial";
}

/**
 * NEW PRODUCT RULE — Trial is guest-only.
 *
 * At the deliberate Trial entry boundary, a validly authenticated user must be
 * routed to their authenticated Dashboard / Full Exam flow instead of starting
 * a guest Trial. This decision intentionally depends on an expiry-aware
 * authentication authority (see `isValidlyAuthenticated`), NOT on mere
 * `access_token` presence — the original incident is precisely that a stale
 * token is present but not valid.
 */
export function shouldRedirectAuthenticatedTrial(input: {
  mode: ExamMode | null;
  isValidlyAuthenticated: boolean;
}): boolean {
  return input.mode === "trial" && input.isValidlyAuthenticated;
}

/**
 * Direct /task-one Trial edge guard.
 *
 * MicTest already clears stale paid credentials before navigating into Trial.
 * A same-tab refresh / history / direct URL to /task-one can preserve
 * `exam_mode=trial` while leaving a stale `access_token` in place. Task 1
 * websocket auth still uses presence-only `isLoggedIn()`, so that stale JWT
 * would be preferred over the guest token created by `startGuestExam`.
 *
 * This helper decides the TaskOne-mount action WITHOUT touching websocket
 * retry/backoff (Fix B). Full Exam / Individual Task return `none`.
 */
export type TaskOneTrialAuthGuardAction =
  | { action: "redirectDashboard" }
  | { action: "clearStaleAccessToken" }
  | { action: "none" };

export function planTaskOneTrialAuthGuard(input: {
  deliberateTrial: boolean;
  isValidlyAuthenticated: boolean;
  hasAccessToken: boolean;
}): TaskOneTrialAuthGuardAction {
  if (!input.deliberateTrial) return { action: "none" };
  if (input.isValidlyAuthenticated) return { action: "redirectDashboard" };
  if (input.hasAccessToken) return { action: "clearStaleAccessToken" };
  return { action: "none" };
}

export function resolveSessionBootstrapPath(input: {
  isDeliberateTrial: boolean;
  isLoggedIn: boolean;
}): SessionBootstrapPath {
  if (input.isDeliberateTrial) return "guest";
  return input.isLoggedIn ? "paid" : "guest";
}

export function planSessionBootstrap(input: {
  isDeliberateTrial: boolean;
  isLoggedIn: boolean;
  existingSessionId: string | null;
  currentExamId: string | null;
}): SessionBootstrapPlan {
  const path = resolveSessionBootstrapPath(input);

  // Deliberate Trial must never reuse paid exam authority (exam_*), whether it
  // comes from current_exam_id or a leftover session_id. Only a guest_* session
  // may be reused so an in-progress Trial can continue across Task 1 → 2 → 3.
  // Non-Trial flows preserve the original reuse behavior for any existing id.
  const canReuse = (id: string | null): id is string => {
    if (!id) return false;
    if (input.isDeliberateTrial) return id.startsWith("guest_");
    return true;
  };

  if (canReuse(input.currentExamId)) {
    return { action: "reuse", sessionId: input.currentExamId };
  }

  if (canReuse(input.existingSessionId)) {
    return { action: "reuse", sessionId: input.existingSessionId };
  }

  return path === "paid"
    ? { action: "startPaidExam" }
    : { action: "startGuestExam" };
}

export function resolveExamAuth(input: {
  isDeliberateTrial: boolean;
  paidToken: string | null;
  guestToken: string | null;
  paidUserId?: string | null;
  guestUserId?: string | null;
}): { isGuest: boolean; token: string | null; userId: string | null } {
  const isGuest =
    input.isDeliberateTrial || (!input.paidToken && !!input.guestToken);

  if (isGuest) {
    return {
      isGuest: true,
      token: input.guestToken || null,
      userId: input.guestUserId || null,
    };
  }

  return {
    isGuest: false,
    token: input.paidToken || input.guestToken || null,
    userId: input.paidUserId || input.guestUserId || null,
  };
}
